import { useState, useEffect } from 'react';
import { ShieldCheck, Clock, Hash, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import { ReadinessDialog } from './ReadinessDialog';
import type { ReadinessSnapshot } from '@/types/api';

export function ReadinessPage() {
  const tz = useDisplayTimezone();
  const { activeProject } = useProject();
  const { canDeclareReadiness } = usePermissions();
  const [snapshots, setSnapshots] = useState<ReadinessSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [showDeclare, setShowDeclare] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!activeProject) return;
    setLoading(true);
    api.readiness
      .list(activeProject.id, showSuperseded)
      .then(setSnapshots)
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  }, [activeProject?.id, showSuperseded, refreshTick]);

  if (!activeProject) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">Select a project to view readiness snapshots.</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading readiness snapshots..." />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            21 CFR Part 11
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">Readiness Snapshots</h1>
          <p className="text-sm text-slate-500 mt-1">
            Immutable records of submission readiness declarations.
          </p>
        </div>
        <button
          onClick={() => setShowDeclare(true)}
          disabled={!canDeclareReadiness}
          title={!canDeclareReadiness ? 'Requires Lead role' : undefined}
          className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50${!canDeclareReadiness ? ' cursor-not-allowed' : ''}`}
        >
          <ShieldCheck className="w-4 h-4" />
          Declare Readiness
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showSuperseded}
            onChange={(e) => setShowSuperseded(e.target.checked)}
            className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
          />
          Show superseded snapshots
        </label>
      </div>

      {snapshots.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            No readiness snapshots yet. Click Declare Readiness above to mint the first snapshot.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {snapshots.map((snapshot) => (
            <SnapshotCard key={snapshot.id} snapshot={snapshot} />
          ))}
        </div>
      )}

      {showDeclare && (
        <ReadinessDialog
          projectId={activeProject.id}
          onClose={() => setShowDeclare(false)}
          onSuccess={() => {
            setShowDeclare(false);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

function SnapshotCard({ snapshot }: { snapshot: ReadinessSnapshot }) {
  const [expanded, setExpanded] = useState(false);
  const tz = useDisplayTimezone();

  return (
    <div
      className={`bg-white rounded-xl border ${
        snapshot.isSuperseded ? 'border-slate-200 opacity-60' : 'border-slate-200'
      } overflow-hidden`}
    >
      <div
        className="px-6 py-4 cursor-pointer hover:bg-slate-50/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className={`w-5 h-5 ${snapshot.isSuperseded ? 'text-slate-400' : 'text-green-500'}`} />
            <div>
              <p className="text-sm font-semibold text-brand-charcoal">
                Readiness Declaration
                {snapshot.isSuperseded && (
                  <span className="ml-2 text-xs font-normal text-slate-400">(superseded)</span>
                )}
              </p>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatInstant(snapshot.signedAt, tz)}
                </span>
                <span className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {snapshot.snapshotHash.slice(0, 12)}...
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {Object.keys(snapshot.documentHashes).length} documents
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-4 space-y-4 border-t border-slate-100">
          {/* Declaration */}
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Declaration</p>
            <p className="text-sm text-amber-800">{snapshot.declarationText}</p>
          </div>

          {/* Prompt versions */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Prompt Versions</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(snapshot.promptVersionsUsed).map(([feature, version]) => (
                <div key={feature} className="flex justify-between text-sm px-3 py-1.5 bg-slate-50 rounded">
                  <span className="text-slate-600">{feature}</span>
                  <span className="font-mono text-slate-800">{version}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hash */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Snapshot Hash</p>
            <p className="font-mono text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded break-all">
              {snapshot.snapshotHash}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
